import React, { useEffect, useMemo, useState } from 'react';
import {
  batchSignTte,
  downloadSignedTte,
  getMySigner,
  getTteDocuments,
  getTteSigners,
  saveTteSigner,
  signTte,
  uploadTteSource
} from '../components/tte/tteApi';
import TtePassphraseModal from '../components/tte/TtePassphraseModal';
import '../styles/tte.css';

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const LABEL = {
  BELUM_SIAP: 'Belum Siap',
  SIAP_TTE: 'Siap TTE',
  PROSES_TTE: 'Proses TTE',
  TERTANDATANGANI: 'Tertandatangani',
  GAGAL_TTE: 'Gagal TTE'
};

export default function TteAdminPage() {
  const role = localStorage.getItem('userRole') || '';
  const [rows, setRows] = useState([]);
  const [signerState, setSignerState] = useState({ is_signer: false, signer: null });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [modal, setModal] = useState({ open: false, pakIds: [] });
  const [signers, setSigners] = useState([]);
  const [showSignerSetup, setShowSignerSetup] = useState(false);
  const [signerForm, setSignerForm] = useState({
    user_id: '',
    bsre_identifier: '',
    nama_lengkap: '',
    jabatan: '',
    pangkat_golongan: '',
    instansi: 'Suku Dinas Pendidikan Wilayah II Kota Administrasi Jakarta Timur',
    is_active: true
  });

  const load = async () => {
    setLoading(true);
    setMessage('');

    try {
      const [docs, me] = await Promise.all([
        getTteDocuments(),
        getMySigner()
      ]);

      setRows(Array.isArray(docs) ? docs : []);
      setSignerState(me || { is_signer: false, signer: null });

      if (role === 'admin') {
        const s = await getTteSigners();
        setSigners(Array.isArray(s) ? s : []);
      }
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const readyIds = useMemo(
    () => rows
      .filter(r => ['SIAP_TTE', 'GAGAL_TTE'].includes(r.tte_status))
      .map(r => Number(r.pak_id)),
    [rows]
  );

  const selectedReady =
    [...selected].filter(id => readyIds.includes(Number(id)));

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const uploadPdf = async (pakId, file) => {
    if (!file) return;

    setBusy(true);
    setMessage('');

    try {
      await uploadTteSource(pakId, file);
      setMessage('PDF berhasil disiapkan untuk TTE.');
      await load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openSign = (ids) => {
    if (!signerState.is_signer) {
      setMessage('Akun ini belum terdaftar sebagai penandatangan TTE.');
      return;
    }

    setModal({ open: true, pakIds: ids });
  };

  const submitSign = async (passphrase) => {
    setBusy(true);
    setMessage('');

    try {
      if (modal.pakIds.length === 1) {
        await signTte(modal.pakIds[0], passphrase);
        setMessage('Dokumen berhasil ditandatangani.');
      } else {
        const result = await batchSignTte(modal.pakIds, passphrase);
        setMessage(
          `Batch selesai: ${result.berhasil || 0} berhasil, ${result.gagal || 0} gagal.`
        );
      }

      setSelected(new Set());
      setModal({ open: false, pakIds: [] });
      await load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const download = async (row) => {
    setBusy(true);

    try {
      const blob = await downloadSignedTte(row.pak_id);
      saveBlob(
        blob,
        row.signed_filename || `PAK-${row.pak_id}-TTE.pdf`
      );
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveSigner = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage('');

    try {
      await saveTteSigner({
        ...signerForm,
        user_id: Number(signerForm.user_id)
      });

      setMessage('Data penandatangan disimpan.');
      setShowSignerSetup(false);
      await load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tte-page">
      <header className="tte-head">
        <div>
          <small>Tanda Tangan Elektronik</small>
          <h1>TTE Dokumen PAK</h1>
          <p>Siapkan PDF final, periksa, lalu tandatangani melalui integrasi BSrE.</p>
        </div>

        <div className="tte-head__actions">
          {selectedReady.length > 0 && (
            <button
              className="tte-btn tte-btn--primary"
              onClick={() => openSign(selectedReady)}
              disabled={busy}
            >
              Tanda Tangani {selectedReady.length} Dokumen
            </button>
          )}

          {role === 'admin' && (
            <button
              className="tte-btn tte-btn--ghost"
              onClick={() => setShowSignerSetup(v => !v)}
            >
              Penandatangan
            </button>
          )}

          <button
            className="tte-btn tte-btn--ghost"
            onClick={load}
            disabled={busy}
          >
            Perbarui
          </button>
        </div>
      </header>

      {message && <div className="tte-message">{message}</div>}

      <section className="tte-summary">
        <div><span>Disetujui</span><strong>{rows.length}</strong></div>
        <div><span>Siap TTE</span><strong>{rows.filter(r => r.tte_status === 'SIAP_TTE').length}</strong></div>
        <div><span>Gagal TTE</span><strong>{rows.filter(r => r.tte_status === 'GAGAL_TTE').length}</strong></div>
        <div><span>Selesai</span><strong>{rows.filter(r => r.tte_status === 'TERTANDATANGANI').length}</strong></div>
      </section>

      {role === 'admin' && showSignerSetup && (
        <section className="tte-setup">
          <div>
            <h3>Pengaturan Penandatangan</h3>
            <p>
              Masukkan user ID SIPAK dan identifier BSrE sesuai data pada proses integrasi.
            </p>
          </div>

          <form onSubmit={saveSigner}>
            <input
              type="number"
              placeholder="User ID SIPAK"
              value={signerForm.user_id}
              onChange={e => setSignerForm({ ...signerForm, user_id: e.target.value })}
              required
            />
            <input
              placeholder="Identifier BSrE"
              value={signerForm.bsre_identifier}
              onChange={e => setSignerForm({ ...signerForm, bsre_identifier: e.target.value })}
              required
            />
            <input
              placeholder="Nama lengkap"
              value={signerForm.nama_lengkap}
              onChange={e => setSignerForm({ ...signerForm, nama_lengkap: e.target.value })}
              required
            />
            <input
              placeholder="Jabatan"
              value={signerForm.jabatan}
              onChange={e => setSignerForm({ ...signerForm, jabatan: e.target.value })}
            />
            <input
              placeholder="Pangkat / golongan"
              value={signerForm.pangkat_golongan}
              onChange={e => setSignerForm({ ...signerForm, pangkat_golongan: e.target.value })}
            />
            <button className="tte-btn tte-btn--primary" disabled={busy}>
              Simpan
            </button>
          </form>

          {signers.length > 0 && (
            <div className="tte-signers">
              {signers.map(s => (
                <div key={s.id}>
                  <strong>{s.nama_lengkap}</strong>
                  <span>{s.jabatan || '-'}</span>
                  <small>{s.is_active ? 'Aktif' : 'Nonaktif'}</small>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {!signerState.is_signer && (
        <div className="tte-note">
          Akun yang sedang masuk belum terdaftar sebagai penandatangan TTE.
          Admin tetap dapat menyiapkan PDF final.
        </div>
      )}

      <section className="tte-table-wrap">
        {loading ? (
          <div className="tte-empty">Memuat data...</div>
        ) : rows.length === 0 ? (
          <div className="tte-empty">Belum ada PAK berstatus Disetujui.</div>
        ) : (
          <table className="tte-table">
            <thead>
              <tr>
                <th></th>
                <th>Guru</th>
                <th>Unit Kerja</th>
                <th>Nomor Surat</th>
                <th>Status TTE</th>
                <th>PDF Final</th>
                <th>Aksi</th>
              </tr>
            </thead>

            <tbody>
              {rows.map(row => {
                const ready =
                  ['SIAP_TTE', 'GAGAL_TTE'].includes(row.tte_status);

                return (
                  <tr key={row.pak_id}>
                    <td>
                      <input
                        type="checkbox"
                        disabled={!ready || !signerState.is_signer}
                        checked={selected.has(Number(row.pak_id))}
                        onChange={() => toggle(Number(row.pak_id))}
                      />
                    </td>

                    <td>
                      <strong>{row.guru_name}</strong>
                      <small>{row.guru_nip || '-'}</small>
                    </td>

                    <td>{row.unit_kerja || '-'}</td>
                    <td>{row.nomor_surat || '-'}</td>

                    <td>
                      <span className={`tte-status tte-status--${(row.tte_status || 'BELUM_SIAP').toLowerCase()}`}>
                        {LABEL[row.tte_status] || 'Belum Siap'}
                      </span>
                      {row.last_error && (
                        <small className="tte-error">{row.last_error}</small>
                      )}
                    </td>

                    <td>
                      {role === 'admin' && (
                        <label className="tte-upload">
                          <input
                            type="file"
                            accept="application/pdf,.pdf"
                            disabled={busy}
                            onChange={e => {
                              const file = e.target.files?.[0];
                              uploadPdf(row.pak_id, file);
                              e.target.value = '';
                            }}
                          />
                          {row.source_filename ? 'Ganti PDF' : 'Upload PDF'}
                        </label>
                      )}
                      {row.source_filename && <small>{row.source_filename}</small>}
                    </td>

                    <td className="tte-actions">
                      {ready && signerState.is_signer && (
                        <button
                          className="tte-btn tte-btn--small"
                          onClick={() => openSign([Number(row.pak_id)])}
                          disabled={busy}
                        >
                          Tanda Tangani
                        </button>
                      )}

                      {row.tte_status === 'TERTANDATANGANI' && (
                        <button
                          className="tte-btn tte-btn--small tte-btn--success"
                          onClick={() => download(row)}
                          disabled={busy}
                        >
                          Unduh TTE
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <TtePassphraseModal
        open={modal.open}
        count={modal.pakIds.length}
        signer={signerState.signer}
        busy={busy}
        onClose={() => !busy && setModal({ open: false, pakIds: [] })}
        onSubmit={submitSign}
      />
    </div>
  );
}
